import { canManageCollection, type Role } from "@/lib/roles";
import type { CollectionDto } from "@/lib/types";

const MAX_AMOUNT_UAH = 1_000_000;

export function parseAmountUah(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    const rounded = Math.round(value);
    if (rounded <= 0 || rounded > MAX_AMOUNT_UAH) return null;
    return rounded;
  }
  const digits = String(value).replace(/[^\d]/g, "");
  if (!digits) return null;
  const parsed = Number.parseInt(digits, 10);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > MAX_AMOUNT_UAH) return null;
  return parsed;
}

export function formatAmountUah(amountUah: number): string {
  return `${amountUah} грн`;
}

const MAX_CARD_DIGITS = 19;

export function cardDigits(value: string): string {
  return value.replace(/\D/g, "").slice(0, MAX_CARD_DIGITS);
}

export function formatCardNumber(value: string): string {
  return cardDigits(value).replace(/(\d{4})(?=\d)/g, "$1 ");
}

export function normalizePayLink(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withProtocol);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    return url.toString().slice(0, 500);
  } catch {
    return "";
  }
}

export function serializeCollection(
  collection: {
    id: string;
    title: string;
    description: string;
    amountUah: number | null;
    payTo: string;
    payName: string;
    payLink: string;
    status: string;
    createdAt: Date;
    createdById: string;
    payments: Array<{ childId: string; child: { name: string; active: boolean } }>;
  },
  linkedChildren: Array<{ id: string; name: string }>,
  roster: Array<{ id: string; name: string }>,
  userId: string,
  role: Role,
): CollectionDto {
  const canManage = canManageCollection(role, collection.createdById, userId);
  const paidIds = new Set(
    collection.payments.filter((payment) => payment.child.active).map((payment) => payment.childId),
  );
  const classSize = roster.length;
  const paidCount = roster.filter((child) => paidIds.has(child.id)).length;

  return {
    id: collection.id,
    title: collection.title,
    description: collection.description,
    amountUah: collection.amountUah,
    payTo: collection.payTo,
    payName: collection.payName,
    payLink: collection.payLink,
    status: collection.status === "closed" ? "closed" : "open",
    createdAt: collection.createdAt.toISOString(),
    paidCount,
    classSize,
    unpaidCount: Math.max(0, classSize - paidCount),
    paidNames: roster.filter((child) => paidIds.has(child.id)).map((child) => child.name),
    roster: canManage
      ? roster.map((child) => ({
          id: child.id,
          name: child.name,
          paid: paidIds.has(child.id),
        }))
      : [],
    myChildren: linkedChildren.map((child) => ({
      id: child.id,
      name: child.name,
      paid: paidIds.has(child.id),
    })),
    canManage,
  };
}
